/**
 * LanguageSwitcher (spec 91 — Wave 0-B).
 *
 * Two shapes:
 *   - `languageMenuItem(t, locale, setLocale)` — a MenuProps item (a "Language"
 *     submenu with EN/ES + a check on the current locale). Inserted into the
 *     AppLayout avatar dropdown after "Profile".
 *   - `<LanguageSwitcher compact />` — a small globe icon + Dropdown, rendered
 *     top-right (absolute) on the three public login pages so pre-auth users can
 *     switch language.
 */

import { Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import { GlobalOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/LocaleProvider';
import type { SupportedLocale } from '../i18n';

type TFn = (key: string) => string;

const LOCALES: SupportedLocale[] = ['en', 'es'];

/**
 * Menu items for the avatar-dropdown "Language" submenu. Returned as a single
 * MenuProps item so the caller can splice it into an existing menu; each child
 * carries a check on the active locale. The onClick is wired at the parent
 * menu level (AppLayout) which routes 'lang:<code>' keys to setLocale.
 */
export function languageMenuItem(
  t: TFn,
  current: SupportedLocale,
): NonNullable<MenuProps['items']>[number] {
  return {
    key: 'language',
    icon: <GlobalOutlined />,
    label: t('language.label'),
    children: LOCALES.map((lng) => ({
      key: `lang:${lng}`,
      label: (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 120,
          }}
        >
          <CheckOutlined
            style={{
              fontSize: 'var(--t-meta)',
              color: 'var(--brand)',
              visibility: lng === current ? 'visible' : 'hidden',
            }}
          />
          <span>{t(`language.${lng}`)}</span>
        </span>
      ),
    })),
  };
}

/**
 * Compact globe switcher for the public login pages. Positioned absolute
 * top-right by the pages that render it.
 */
export default function LanguageSwitcher({
  compact = false,
}: {
  compact?: boolean;
}) {
  const { t } = useTranslation('common');
  const { locale, setLocale } = useLocale();

  const items: MenuProps['items'] = LOCALES.map((lng) => ({
    key: lng,
    label: (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 120,
        }}
      >
        <CheckOutlined
          style={{
            fontSize: 'var(--t-meta)',
            color: 'var(--brand)',
            visibility: lng === locale ? 'visible' : 'hidden',
          }}
        />
        <span>{t(`language.${lng}`)}</span>
      </span>
    ),
  }));

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'en' || key === 'es') setLocale(key);
  };

  if (compact) {
    return (
      <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight">
        <Button
          type="text"
          aria-label={t('language.label')}
          icon={<GlobalOutlined style={{ fontSize: 'var(--t-h2)' }} />}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {t(`language.${locale}`)}
        </Button>
      </Dropdown>
    );
  }

  return (
    <Dropdown menu={{ items, onClick }} trigger={['click']}>
      <Button type="text" icon={<GlobalOutlined />}>
        {t('language.label')}
      </Button>
    </Dropdown>
  );
}
